/* eslint-disable react-func/max-lines-per-function */
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { sendEvent } from 'common/tracker';
import { CustomEvent } from 'common/tracker/custom-event';
import { AccountType } from 'common/types';
import { Alert } from 'antd';
import { eventEmitter } from '@/utils/eventEmitter';
import { useAppDispatch } from '@/hooks';
// import { getTaskQueue } from '@/store/taskQueue';

const NO_PROVIDER = {};
const WebsocketContext = createContext<{ isWSConnected?: boolean; socketRef?: React.MutableRefObject<any>['current'] }>(
  NO_PROVIDER,
);

type Props = { isLogin: boolean; userInfo?: AccountType };

const WebsocketProvider: React.FC<React.PropsWithChildren<Props>> = ({ isLogin, userInfo, children }) => {
  const dispatch = useAppDispatch();
  const [isWSConnected, setIsWSConnected] = useState(false);
  const [isConnectError, setIsConnectError] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const socketRef = useRef<any>();

  // 页面可见性状态发生变更重新可见时，如果 ws 已经断开连接，则重新连接 ws
  useEffect(() => {
    if (!isLogin || !userInfo?.uniqueId) {
      return;
    }
    console.log('in context try to connect', isLogin, userInfo);
    // const { wsServer = 'https://tiangong-websocket.apps-sl.danlu.netease.com' } = getEnv();
    const socket = io('/', {
      query: {
        userId: userInfo?.uniqueId,
      },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;
    socket.on('connect', () => {
      console.log('%cOnConnect:' + socket.id, 'color: green; font-size: 16px;font-weight: bold;');
      // dispatch(setIsAceConnect(true));
      setIsWSConnected(true);
      setIsConnectError(false);
    });

    socket.on('ON_MESSAGE', (msg, acknowledge) => {
      console.log('Received message from client:', { type: msg.type, msg: JSON.parse(msg.data) });
      // switch (msg.type) {
      // case WsEventTypes.TaskQueueUpdateStatus:
      //   eventEmitter.emit(WsEventTypes.TaskQueueUpdateStatus, JSON.parse(msg.data));
      //   break;
      // case WsEventTypes.Alert:
      //   eventEmitter.emit(WsEventTypes.Alert, JSON.parse(msg.data));
      // case WsEventTypes.InstanceReady:
      //   eventEmitter.emit(WsEventTypes.InstanceReady, JSON.parse(msg.data));
      //   break;
      // case WsEventTypes.VersionChange:
      //   eventEmitter.emit(WsEventTypes.VersionChange, JSON.parse(msg.data));
      //   break;

      // }
      eventEmitter.emit(msg.type, JSON.parse(msg.data));

      // 处理消息并发送回客户端
      if (typeof acknowledge === 'function') {
        acknowledge(`${msg.type} received and processed`);
      }
    });
    // socket.on(WsEventTypes.MessageChange, (msg, acknowledge) => {
    //   console.log('Received message from client:', msg);
    //   eventEmitter.emit(WsEventTypes.MessageChange, JSON.parse(msg.data));
    //   // 处理消息并发送回客户端
    //   if (typeof acknowledge === 'function') {
    //     acknowledge(`${WsEventTypes.MessageChange} received and processed`);
    //   }
    // });

    socket.on('connect_error', (res) => {
      console.error('connect_error', { res, socket });
      setIsWSConnected(false);
      setIsConnectError(true);
      // setTimeout(() => {
      //   socket.connect();
      // }, 1000);
    });
    socket.on('disconnect', (reason) => {
      console.log('%cDisConnect:' + socket.id, 'color: red; font-size: 16px;font-weight: bold;');
      console.log('reason', reason); // false
      setIsWSConnected(false);
      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        socket.connect();
      }
    });
    const handleVisibilityChange = () => {
      const connected = socket.connected;
      sendEvent(CustomEvent.WsConnect, {
        et: 'CLK',
        c1: { documentVisibilityState: document.visibilityState, wsState: connected, time: new Date() },
      });
      if (document.visibilityState === 'visible') {
        console.log('Visible:', connected, new Date());
        // dispatch(getTaskQueue());
        setIsWSConnected(true);
      } else {
        console.log('disVisible:', connected, new Date());
        setIsWSConnected(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // 摘掉自己注册的监听并断开连接：effect 重跑（isLogin / userId 变化）时若不断开，
      // 旧 socket 会连同它的 ON_MESSAGE 一起残留，同一条消息会被重复 emit 到 eventEmitter
      socket.off('connect');
      socket.off('ON_MESSAGE');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.disconnect();
      setIsConnectError(false);
      setIsWSConnected(false);
      setIsNetworkError(false);
    };
  }, [isLogin, userInfo?.uniqueId]);

  useEffect(() => {
    const handleOffline = () => {
      console.log('离线');
      setIsNetworkError(true);
    };
    const handleOnline = () => {
      console.log('在线');
      setIsNetworkError(false);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // 不 memo 的话每次渲染都是新对象，WebsocketProvider 一重渲染就会带着整个路由树一起重渲染
  const contextValue = useMemo(() => ({ isWSConnected, socketRef: socketRef.current }), [isWSConnected]);

  return (
    <WebsocketContext.Provider value={contextValue}>
      {(isConnectError || isNetworkError) && (
        <Alert
          message={`网络连接异常断开${isConnectError ? '（推送服务异常）' : ''}，请尝试刷新页面重新连接`}
          type="error"
          showIcon
          closable
        />
      )}

      {children}
    </WebsocketContext.Provider>
  );
};

const useWebsocketContext = () => {
  const context = React.useContext(WebsocketContext);
  if (context === NO_PROVIDER) {
    throw new Error('useWebsocketContext must be used within a WebsocketProvider');
  }
  return context;
};

export { WebsocketProvider, useWebsocketContext };
